import { useEffect, useState } from 'react';
import memoizeOne from 'memoize-one';

import { invoiceRequests_get_v1 } from '@atlassian/commerce-ccp-gateway-client';
import type { InvoiceRequestListDto } from '@atlassian/commerce-ccp-gateway-client';
import { getSuccessfulResultOrThrow } from '@atlassian/commerce-ccp-gateway-client/error-wrappers';

import { cfetch } from '@confluence/network';

import { getCurrentEntitlementDetails } from './getCurrentEntitlementDetails.ts';

const memoizedInvoiceRequests_get_v1 = memoizeOne(
	invoiceRequests_get_v1,
	(
		[
			{
				headers: { 'X-transaction-account': newTransactionAccountId },
			},
		],
		[
			{
				headers: { 'X-transaction-account': lastTransactionAccountId },
			},
		],
	) => {
		return newTransactionAccountId === lastTransactionAccountId;
	},
);

export interface UseInvoiceRequestResponse {
	entitlementId: string | undefined;
	invoiceRequestData: InvoiceRequestListDto | null;
	loading: boolean;
	error: Error | null;
}

export const useInvoiceRequestV2 = (
	{ skip }: { skip: boolean } = { skip: false },
): UseInvoiceRequestResponse => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [entitlementId, setEntitlementId] = useState<string | undefined>(undefined);
	const [invoiceRequestData, setInvoiceRequestData] = useState<InvoiceRequestListDto | null>(null);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const { transactionAccountId, subscriptionv2, entitlementId } =
					(await getCurrentEntitlementDetails()) ?? {};

				const invoiceGroupId = subscriptionv2?.accountDetails?.invoiceGroupId ?? '';

				if (!transactionAccountId) throw new Error('empty transactionAccountId');
				if (!invoiceGroupId) throw new Error('empty invoiceGroupId');
				if (!entitlementId) throw new Error('empty entitlementId');

				const invoiceRequestResponse: InvoiceRequestListDto = getSuccessfulResultOrThrow(
					await memoizedInvoiceRequests_get_v1({
						fetch: cfetch,
						headers: {
							'X-transaction-account': transactionAccountId,
						},
						queryParameters: {
							invoiceGroup: invoiceGroupId,
							statusFilter: 'OPEN',
							// recommended limit of 50
							pageRequest: { pageSize: 50 },
						},
					}),
				);

				setInvoiceRequestData(invoiceRequestResponse);
				setEntitlementId(entitlementId);
			} catch (error) {
				error.message = `useInvoiceRequest: ${error.message}`;
				setError(error);
			} finally {
				setLoading(false);
			}
		};

		if (!skip) {
			void fetchData();
		}
	}, [skip]);

	return {
		entitlementId,
		invoiceRequestData,
		error,
		loading: skip ? false : loading,
	};
};
